/**
 * Preferences Context
 * Manages user preferences (theme, etc.)
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface Preferences {
  darkMode: boolean;
}

interface PreferencesContextType {
  preferences: Preferences;
  toggleDarkMode: () => void;
  setDarkMode: (value: boolean) => void;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

// Initialize preferences from localStorage or system preference
function initializePreferences(): Preferences {
  if (typeof window === 'undefined') {
    return { darkMode: false };
  }
  
  const stored = localStorage.getItem('netra_preferences');
  if (stored) {
    try {
      return JSON.parse(stored) as Preferences;
    } catch (e) {
      // Fallback to system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return { darkMode: prefersDark };
    }
  }
  
  // Use system preference
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return { darkMode: prefersDark };
}

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize with stored or system preference immediately
  const [preferences, setPreferences] = useState<Preferences>(initializePreferences);

  // Apply dark mode to document element and localStorage
  useEffect(() => {
    localStorage.setItem('netra_preferences', JSON.stringify(preferences));
    if (preferences.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [preferences]);

  const toggleDarkMode = useCallback(() => {
    setPreferences((prev) => ({ ...prev, darkMode: !prev.darkMode }));
  }, []);

  const setDarkMode = useCallback((value: boolean) => {
    setPreferences((prev) => ({ ...prev, darkMode: value }));
  }, []);

  return (
    <PreferencesContext.Provider value={{ preferences, toggleDarkMode, setDarkMode }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
};
